import React, { useState } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(timezone);
dayjs.extend(utc);

const getLocalTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const createLocalDate = (date?: string | Date | dayjs.Dayjs) => {
  const localTz = getLocalTimezone();
  if (date) return dayjs(date).tz(localTz);
  return dayjs().tz(localTz);
};

export interface ScheduleCalendarProps {
  selectedDate: dayjs.Dayjs | null;
  onChange: (date: dayjs.Dayjs) => void;
  viewMode?: 'day' | 'month';
  timezone?: string;
}

export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  selectedDate,
  onChange,
  viewMode = 'day',
  timezone: tz,
}) => {
  const localNow = createLocalDate();
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate ? selectedDate.month() : localNow.month()
  );
  const [currentYear, setCurrentYear] = useState(
    selectedDate ? selectedDate.year() : localNow.year()
  );
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const daysInMonth = createLocalDate().year(currentYear).month(currentMonth).daysInMonth();
  const firstDayOfMonth = createLocalDate()
    .year(currentYear)
    .month(currentMonth)
    .startOf('month')
    .day();
  const daysGrid = Array.from({ length: 42 }, (_, i) => {
    const adjustedIndex = i - firstDayOfMonth + 1;
    return adjustedIndex > 0 && adjustedIndex <= daysInMonth ? adjustedIndex : null;
  });

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else setCurrentMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else setCurrentMonth((m) => m + 1);
  };
  const prevYear = () => setCurrentYear((y) => y - 1);
  const nextYear = () => setCurrentYear((y) => y + 1);

  const handleDateClick = (day: number) => {
    const baseDate = selectedDate
      ? selectedDate.clone()
      : tz
        ? dayjs().tz(tz)
        : createLocalDate();
    const newDate = baseDate.year(currentYear).month(currentMonth).date(day);
    onChange(newDate);
  };

  const handleMonthClick = (monthIndex: number) => {
    const newDate = createLocalDate()
      .year(currentYear)
      .month(monthIndex)
      .endOf('month');
    onChange(newDate);
  };

  const isToday = (day: number) => {
    const today = createLocalDate();
    return (
      day === today.date() &&
      currentMonth === today.month() &&
      currentYear === today.year()
    );
  };
  const isSelected = (day: number) => {
    if (!selectedDate) return false;
    return (
      day === selectedDate.date() &&
      currentMonth === selectedDate.month() &&
      currentYear === selectedDate.year()
    );
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  if (viewMode === 'month') {
    return (
      <div className="custom-calendar month-view broadcast-schedule-calendar">
        <div className="calendar-header">
          <button type="button" className="calendar-nav-button" onClick={prevYear}>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="none" stroke="currentColor" strokeWidth="2" d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className="calendar-month-year">
            <span>{currentYear}</span>
          </div>
          <button type="button" className="calendar-nav-button" onClick={nextYear}>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="none" stroke="currentColor" strokeWidth="2" d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
        <div className="calendar-months-grid">
          {monthNames.map((mName, idx) => (
            <div
              key={idx}
              className={`calendar-month-item ${
                selectedDate && selectedDate.month() === idx && selectedDate.year() === currentYear
                  ? 'selected'
                  : ''
              }`}
              onClick={() => handleMonthClick(idx)}
              onKeyDown={(e) => e.key === 'Enter' && handleMonthClick(idx)}
              role="button"
              tabIndex={0}
            >
              <span>{mName.substring(0, 3)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="custom-calendar day-view broadcast-schedule-calendar">
      <div className="calendar-header">
        <button type="button" className="calendar-nav-button" onClick={prevMonth}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="none" stroke="currentColor" strokeWidth="2" d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="calendar-month-year">
          <span>{monthNames[currentMonth]} {currentYear}</span>
        </div>
        <button type="button" className="calendar-nav-button" onClick={nextMonth}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="none" stroke="currentColor" strokeWidth="2" d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
      <div className="calendar-weekdays">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
          <div key={index} className="calendar-weekday">
            <span>{day}</span>
          </div>
        ))}
      </div>
      <div className="calendar-days">
        {daysGrid.map((day, index) => (
          <div
            key={index}
            className={`calendar-day ${day === null ? 'empty' : ''} ${
              day !== null && isToday(day) ? 'today' : ''
            } ${day !== null && isSelected(day) ? 'selected' : ''} ${
              day !== null && hoveredDay === day && !isSelected(day) ? 'hovered' : ''
            }`}
            onClick={() => day !== null && handleDateClick(day)}
            onMouseEnter={() => day !== null && setHoveredDay(day)}
            onMouseLeave={() => setHoveredDay(null)}
          >
            {day !== null ? <span>{day}</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScheduleCalendar;
